import { Suspense, lazy, useEffect, useState } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { useAppSelector } from '../types/hooks';

const LoginPage = lazy(() => import('../pages/Guest/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../pages/Guest/ForgotPasswordPage'));
const NoPage = lazy(() => import('../pages/NoPage'));
const Account = lazy(() => import('../pages/Account/Account'));
const MapPage = lazy(() => import('../pages/MapPage'));
const ChangeLanguagePage = lazy(() => import('../pages/Settings/ChangeLanguagePage'));
const DevicesPage = lazy(() => import('../pages/Device/DevicesPage'));
const AppSettingsPage = lazy(() => import('../pages/Settings/AppSettingsPage'));

import ErrorBoundary from './ErrorBoundary';
import GuestFallback from '../components/Layout/GuestFallback';
import HunterFallback from '../components/Layout/HunterFallback';

export const APP_ROUTES = {
    home: '/',
    login: '/login',
    pricelist: '/pricelist',
    account: '/account',
    licences: '/licences',
    calendar: '/calendar',
    huntingGrounds: '/hunting-grounds',
    geofences: '/geofences',
    teams: '/teams',
    devices: '/devices',
    team: '/team',
    teamCreate: '/team-create',
    hunts: '/hunts',
    huntCreate: '/hunt-create',
    huntDetails: '/hunt',
    map: '/map',
    createGround: '/ground-create',
    changeLanguage: '/language',
    support: '/support',
    forgot: '/forgot',
    deviceInfo: '/device-info',
    deviceProfile: '/device-profile',
    deviceSettings: '/device-settings',
    deviceAttributes: '/device',
    subscriptions: '/subscriptions',
    appSettings: '/settings',
    contacts: '/contacts',
    contactGroups: '/contact-groups',
    contactGroup: '/contact-group',
    contactsForGroup: '/group-contacts',
    contact: '/contact',
    notificationSettings: '/notifications',
    createGroup: '/group-create',
    chats: '/chats',
    chat: '/chat',
    groupChat: '/group-chat',
    groupUsers: '/group-users',
    newGroupChat: '/group-new',
};

export const languages = ['en', 'sv'];
// export const languages = ['en', 'es', 'de', 'da', 'el', 'fr', 'fi', 'it', 'no', 'pl', 'sv'];

export function generateRoutes(userId) {
    let routes = [
        {
            path: `/${APP_ROUTES.login}`,
            element: (
                <Suspense fallback={<GuestFallback />}>
                    <LoginPage />
                </Suspense>
            ),
        },
        {
            path: `/${APP_ROUTES.forgot}`,
            element: (
                <Suspense fallback={<GuestFallback />}>
                    <ForgotPasswordPage />
                </Suspense>
            ),
        },
        {
            path: APP_ROUTES.appSettings,
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <AppSettingsPage />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
        {
            path: APP_ROUTES.changeLanguage,
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <ChangeLanguagePage />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
        {
            path: APP_ROUTES.devices,
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <DevicesPage />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
        {
            path: APP_ROUTES.map,
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <MapPage />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
        {
            path: APP_ROUTES.account,
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <Account />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
        {
            path: '*',
            element: (
                <Suspense fallback={<HunterFallback />}>
                    <NoPage />
                </Suspense>
            ),
            errorElement: <ErrorBoundary />,
        },
    ];

    if (typeof userId != 'undefined') {
        if (userId > 0) {
            routes.unshift({
                path: APP_ROUTES.home,
                element: (
                    <Suspense fallback={<HunterFallback />}>
                        <MapPage />
                    </Suspense>
                ),
            });
        } else {
            routes.unshift({
                path: APP_ROUTES.home,
                element: (
                    <Suspense fallback={<GuestFallback />}>
                        <LoginPage />
                    </Suspense>
                ),
            });
            languages.forEach((prefix) => {
                routes.unshift({
                    path: `/${prefix}`,
                    element: (
                        <Suspense fallback={<GuestFallback />}>
                            <LoginPage />
                        </Suspense>
                    ),
                });
            });
        }
    }

    languages.forEach((prefix) => {
        routes.push(
            {
                path: `/${prefix}${APP_ROUTES.login}`,
                element: (
                    <Suspense fallback={<GuestFallback />}>
                        <LoginPage />
                    </Suspense>
                ),
            },
            {
                path: `/${prefix}${APP_ROUTES.forgot}`,
                element: (
                    <Suspense fallback={<GuestFallback />}>
                        <ForgotPasswordPage />
                    </Suspense>
                ),
            },
        );
    });

    return createBrowserRouter(routes);
}

// eslint-disable-next-line no-undef
export const useDynamicRouter = (userId) => {
    const [router, setRouter] = useState(generateRoutes(userId));

    useEffect(() => {
        setRouter(generateRoutes(userId));
    }, [userId]);

    return router;
};
